import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { formatTime, utcToAucklandInput } from "../../../shared/time";
import type { CalendarItem, Resource, Room } from "../../../shared/types";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { EventForm } from "./EventForm";
import { PageFrame, hasRole, messageOf } from "./chrome";
import { addDays, dayLabel, dayNumber, monthGrid, monthTitle, overlapsDay, rangeFor, shiftMonthKey, todayKey, weekKeys } from "./dates";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FILTERS = ["all", "gathering", "rehearsal", "registration", "meeting", "plan"] as const;

function clashIds(items: CalendarItem[]): Set<string> {
  const clashes = new Set<string>();
  const bookings = items.filter((item) => item.roomId && item.status !== "cancelled" && item.kind !== "plan");
  for (let left = 0; left < bookings.length; left += 1) {
    for (let right = left + 1; right < bookings.length; right += 1) {
      const a = bookings[left];
      const b = bookings[right];
      if (a.roomId === b.roomId && a.startsAt < b.endsAt && a.endsAt > b.startsAt) {
        clashes.add(a.id);
        clashes.add(b.id);
      }
    }
  }
  return clashes;
}

function chipClass(item: CalendarItem, clashes: Set<string>): string {
  const names = ["chip", item.kind];
  if (item.status === "cancelled") names.push("cancelled");
  if (clashes.has(item.id)) names.push("clash");
  return names.join(" ");
}

function openHref(item: CalendarItem): string {
  return item.kind === "plan" ? item.href : `/calendar/${item.id}`;
}

export function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "coordinator");
  const [view, setView] = useState<"month" | "week" | "list">("month");
  const [anchor, setAnchor] = useState(todayKey);
  const [selected, setSelected] = useState(todayKey);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState<string | null>(null);

  const bounds = useMemo(() => {
    const days = view === "week" ? weekKeys(anchor) : monthGrid(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)));
    const last = days[days.length - 1];
    return { days, ...rangeFor(days[0], last) };
  }, [anchor, view]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void api<CalendarItem[]>(`/api/v1/calendar?from=${encodeURIComponent(bounds.from)}&to=${encodeURIComponent(bounds.to)}`)
      .then((rows) => { if (live) setItems(rows); })
      .catch((caught: unknown) => { if (live) setError(messageOf(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [bounds.from, bounds.to]);

  useEffect(() => {
    void api<{ rooms: Room[]; resources: Resource[] }>("/api/v1/rooms")
      .then((catalog) => { setRooms(catalog.rooms); setResources(catalog.resources); })
      .catch(() => { setRooms([]); setResources([]); });
  }, []);

  const clashes = useMemo(() => clashIds(items), [items]);
  const roomNames = useMemo(() => new Map(rooms.map((room) => [room.id, room.name])), [rooms]);
  const shown = items.filter((item) => {
    if (filter !== "all" && item.kind !== filter) return false;
    if (query.trim() && !`${item.title} ${item.place}`.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  }).slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const selectedItems = shown.filter((item) => overlapsDay(item.startsAt, item.endsAt, selected));
  const heading = view === "week" ? `${dayLabel(bounds.days[0])} – ${dayLabel(bounds.days[6])}` : monthTitle(anchor);

  function move(delta: number) {
    const next = view === "week" ? addDays(anchor, delta * 7) : shiftMonthKey(anchor, delta);
    setAnchor(next);
    setSelected(view === "week" ? next : `${next.slice(0, 7)}-${selected.slice(-2)}`);
  }

  return (
    <PageFrame
      kicker="Operations"
      title="Calendar"
      lede="Events and plans on Auckland time. A room or shared resource already in use is rejected when you save."
      actions={canEdit ? <button className="btn primary" type="button" onClick={() => setCreating(selected)}>Schedule event</button> : <p className="notice">Scheduling needs a coordinator login.</p>}
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="split">
        <div className="cluster">
          <button className="btn small" type="button" onClick={() => move(-1)}>Previous</button>
          <button className="btn small ghost" type="button" onClick={() => { const today = todayKey(); setAnchor(today); setSelected(today); }}>Today</button>
          <button className="btn small" type="button" onClick={() => move(1)}>Next</button>
          <strong>{heading}</strong>
          {loading ? <span className="faint">Updating…</span> : <span className="faint">{shown.length} in view</span>}
        </div>
        <div className="ops-switch" role="group" aria-label="Calendar view">
          {(["month", "week", "list"] as const).map((mode) => (
            <button key={mode} className="btn small" type="button" aria-pressed={view === mode} onClick={() => setView(mode)}>{mode}</button>
          ))}
        </div>
      </div>
      <div className="cluster">
        <div className="ops-switch" role="group" aria-label="Filter kind">
          {FILTERS.map((kind) => (
            <button key={kind} className="btn small ghost" type="button" aria-pressed={filter === kind} onClick={() => setFilter(kind)}>{kind}</button>
          ))}
        </div>
        <label className="grow field">
          <span className="faint">Filter titles</span>
          <input aria-label="Filter titles in this range" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search this range" />
        </label>
      </div>
      <div className="cal-board">
        <div>
          {view === "month" && (
            <div className="cal-scroll">
              <p className="cal-hint">Swipe to see all seven days →</p>
              <div className="cal-grid" style={{ marginBottom: 6 }}>{WEEKDAYS.map((day) => <div key={day} className="cal-dow">{day}</div>)}</div>
              <div className="cal-grid">
                {bounds.days.map((day) => {
                  const dayItems = shown.filter((item) => overlapsDay(item.startsAt, item.endsAt, day));
                  return (
                    <div key={day} className={`cal-cell${day.slice(0, 7) === anchor.slice(0, 7) ? "" : " outside"}${day === todayKey() ? " today" : ""}${day === selected ? " selected" : ""}`}>
                      <button className="cal-date" type="button" onClick={() => setSelected(day)}>{dayNumber(day)}</button>
                      {dayItems.slice(0, 3).map((item) => (
                        <Link key={item.id} className={chipClass(item, clashes)} to={openHref(item)} title={`${formatTime(item.startsAt)} ${item.title}`}>{formatTime(item.startsAt)} {item.title}</Link>
                      ))}
                      {dayItems.length > 3 ? <button className="quiet faint" type="button" onClick={() => setSelected(day)}>+{dayItems.length - 3} more</button> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {view === "week" && (
            <div className="week-grid">
              {bounds.days.map((day) => (
                <div key={day} className={`panel panel-pad week-col${day === selected ? " selected" : ""}`}>
                  <button className="cal-date" type="button" onClick={() => setSelected(day)}>{dayLabel(day)}</button>
                  {shown.filter((item) => overlapsDay(item.startsAt, item.endsAt, day)).map((item) => (
                    <Link key={item.id} className={chipClass(item, clashes)} to={openHref(item)}>{formatTime(item.startsAt)} {item.title}</Link>
                  ))}
                </div>
              ))}
            </div>
          )}
          {view === "list" && (
            <div className="panel table-wrap">
              {shown.length === 0 ? <Empty title="Nothing in this range" body="Move the dates or schedule an event." /> : (
                <table className="data">
                  <thead><tr><th>When</th><th>Title</th><th>Kind</th><th>Place</th><th>Room</th><th>Status</th></tr></thead>
                  <tbody>
                    {shown.map((item) => (
                      <tr key={`${item.kind}-${item.id}`}>
                        <td className="time">{dayLabel(utcToAucklandInput(item.startsAt).slice(0, 10))}<div>{formatTime(item.startsAt)}</div></td>
                        <td><Link to={openHref(item)}>{item.title}</Link>{item.kind !== "plan" && item.href.startsWith("/services/") ? <div><Link to={item.href}>Open plan</Link></div> : null}</td>
                        <td>{item.kind}</td>
                        <td>{item.place || "—"}</td>
                        <td>{item.roomId ? roomNames.get(item.roomId) ?? "Room" : "—"}{clashes.has(item.id) ? <Badge tone="danger">Room clash</Badge> : null}</td>
                        <td><Badge tone={item.status === "cancelled" ? "danger" : item.status === "draft" ? "amber" : "teal"}>{item.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        <aside className="stack">
          <article className="panel panel-pad stack">
            <h2>{dayLabel(selected)}</h2>
            {selectedItems.length === 0 && <p className="muted">Nothing on this day.</p>}
            {selectedItems.map((item) => (
              <div key={`${item.kind}-${item.id}`} className="stack">
                <Link to={openHref(item)}><strong>{item.title}</strong></Link>
                <div className="faint">{formatTime(item.startsAt)}–{formatTime(item.endsAt)} · {item.place || item.kind}</div>
                {item.roomId ? <div className="muted">{roomNames.get(item.roomId) ?? "Room"}{clashes.has(item.id) ? " · overlaps another booking" : ""}</div> : null}
                {item.kind !== "plan" && item.href.startsWith("/services/") ? <Link to={item.href}>Open the plan</Link> : null}
              </div>
            ))}
          </article>
          <article className="panel panel-pad stack">
            <h2>Rooms and resources</h2>
            <p className="muted">Book these on an event. The catalog itself cannot be added to or renamed from this app — that write API is not there.</p>
            {rooms.map((room) => {
              const bookings = items.filter((item) => item.roomId === room.id && item.kind !== "plan" && item.status !== "cancelled");
              return (
                <div key={room.id}>
                  <strong>{room.name}</strong>
                  <div className="faint">{room.capacity ?? "—"} seats{room.notes ? ` · ${room.notes}` : ""}</div>
                  {bookings.length === 0 ? <div className="muted">Free in this range</div> : bookings.map((item) => (
                    <div key={item.id} className="muted">{formatTime(item.startsAt)} {item.title}{clashes.has(item.id) ? " · clash" : ""}</div>
                  ))}
                </div>
              );
            })}
            {resources.map((resource) => <div key={resource.id}><strong>{resource.name}</strong><div className="faint">{resource.kind}{resource.notes ? ` · ${resource.notes}` : ""}</div></div>)}
          </article>
        </aside>
      </div>
      {creating !== null && (
        <Dialog title="Schedule an event" onClose={() => setCreating(null)}>
          <EventForm seedDay={creating} onClose={() => setCreating(null)} onSaved={(event) => { setCreating(null); navigate(`/calendar/${event.id}`); }} />
        </Dialog>
      )}
    </PageFrame>
  );
}
